import type { CopilotAnswer } from './copilotTypes';
import histogram from './__fixtures__/histogram.json';
import calendar from './__fixtures__/calendar_heatmap.json';
import kpi from './__fixtures__/kpi.json';
import trend from './__fixtures__/trend_line.json';
import scatter from './__fixtures__/scatter_regression.json';
import stacked from './__fixtures__/stacked_area.json';
import polar from './__fixtures__/polar_bar.json';
import bubble from './__fixtures__/bubble.json';

/** Normalizes a question for cache lookup (lowercase, strip punctuation, collapse spaces). */
export const normalizeQuestion = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();

/**
 * The scripted demo beats, mapped to the golden fixtures. Lets the tab answer the scripted questions
 * instantly + deterministically (no live LLM); anything off-script falls through to the live route.
 */
const BEATS: Array<{ q: string; a: CopilotAnswer }> = [
  { q: "Give me a snapshot of the cohort's last 6 months", a: kpi as any },
  { q: 'Distribution of daily step counts across the cohort', a: histogram as any },
  { q: "How consistent has the cohort's activity been this year", a: calendar as any },
  { q: 'Resting heart rate trend across the cohort over 6 months', a: trend as any },
  { q: 'Is there a relationship between steps and sleep across participants', a: scatter as any },
  { q: 'Break down sleep stages across the cohort', a: stacked as any },
  { q: 'Which days of the week is the cohort most active', a: polar as any },
  { q: 'VO2 max vs exercise across participants as a bubble chart', a: bubble as any },
];

export const EXAMPLE_QUESTIONS = BEATS.map((b) => b.q);

export const DEMO_CACHE: Record<string, CopilotAnswer> = Object.fromEntries(
  BEATS.map((b) => [normalizeQuestion(b.q), b.a])
);

/**
 * Client-side follow-up chips, keyed on the RENDERED answer — the spine has no server-proposed
 * suggestions yet, and when a chart falls back the server's chart-specific suggestions are dropped,
 * so this is the only source of follow-ups. Keyed on `spec.chart` for chart turns, `kind` otherwise.
 */
export function suggestFollowups(answer: CopilotAnswer | undefined): string[] {
  if (!answer) return [];
  if (answer.kind === 'chart') {
    const chart = (answer.spec as any)?.chart as string | undefined;
    switch (chart) {
      case 'histogram':
        return ['Show this as a trend over time', 'How does this compare week to week?'];
      case 'trend_line':
        return ['Show the distribution instead', 'Is the trend going up or down?'];
      case 'kpi':
        return ['Break this down by metric', 'How does this compare to last month?'];
      case 'calendar_heatmap':
        return ['Which days are most active?', 'Show this as a trend'];
      case 'scatter_regression':
      case 'bubble':
        return ['How strong is that relationship?', 'Show each metric on its own'];
      case 'stacked_area':
        return ['Show total sleep over time', 'Which stage changed the most?'];
      case 'polar_bar':
        return ['Show this as a trend over time', 'Which day stands out?'];
      default:
        return ['Show this a different way', 'Compare across the last 90 days'];
    }
  }
  if (answer.kind === 'escape') return ['Show this a different way', 'Summarize the key takeaway'];
  return []; // fallback table: no useful client follow-up
}
